import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import Button from '../Button';
import Spacer from '../Spacer';
import { hexify } from '../../utils';
import announcementTypes, { VIEW_STATE } from './announcementTypes';
import Info from '../../svg/icons/ic_info-outline_red_35x35.svg';
import Icon from '../Icon';

import './Announcement.scss';

const { HEADER, POPUP, FOOTER } = announcementTypes;

const AnnouncementContent = ({
  heading,
  title,
  content,
  image,
  primaryLinkText,
  secondaryLinkText,
  onPrimaryLinkClick,
  onSecondaryLinkClick,
  colors,
  mobile,
  type,
  viewState,
  operation,
}) => {
  const isDefaultView = viewState === VIEW_STATE.DEFAULT;
  const isLoadingView = viewState === VIEW_STATE.LOADING;
  const isSuccessView = viewState === VIEW_STATE.SUCCESS;
  const isErrorView = viewState === VIEW_STATE.ERROR;

  const announcementImage = () => (
    image && type === POPUP && (
      <div className="AnnouncementContent__image">
        <img src={image} alt="Announcement" />
      </div>
    )
  );

  const announcementHeading = (headingText) => (
    headingText && (
      <div
        className={cx('AnnouncementContent__heading', {
          isloading: isLoadingView,
        })}
        style={{
          color: hexify(colors.heading),
        }}
      >
        {headingText}
      </div>
    )
  );

  const announcementTitle = (titleText) => (
    <div
      className={cx('AnnouncementContent__title', {
        isloading: isLoadingView,
      })}
      style={{
        color: hexify(colors.title),
      }}
    >
      {titleText}
    </div>
  );

  const announcementContent = (contentText) => (
    <div
      className={cx('AnnouncementContent__content', {
        isloading: isLoadingView,
      })}
      style={{
        color: hexify(colors.font),
      }}
    >
      {contentText}
    </div>
  );

  const translatedCloseText = getTranslation({ translationKey: 'close', fallback: 'Close' });

  const announcementButtonContainer = () => (
    <div className="AnnouncementContent__button-container">
      <Button
        onClick={onPrimaryLinkClick}
        colors={colors.primaryButton}
        processing={isLoadingView}
        data-test-id="announcement-button-primary"
      >
        {primaryLinkText || translatedCloseText}
      </Button>

      <div>
        {secondaryLinkText
        && (
          <>
            <Spacer height={10} />
            <Button
              onClick={onSecondaryLinkClick}
              colors={colors.secondaryButton}
              variant="secondary"
              data-test-id="announcement-button-secondary"
              disabled={isLoadingView}
            >
              {secondaryLinkText || translatedCloseText}
            </Button>
          </>
        )}
      </div>
    </div>
  );

  const announcementLegalDisclaimer = () => (
    <div
      className={cx({
        isloading: isLoadingView,
      })}
    >
      <p className="AnnouncementContent__disclaimer">

        You agree to the use of your data as described in our
        {' '}
        <a className={cx('AnnouncementContent__disclaimer-link', { isloading: isLoadingView })} href="/privacy" target="_blank">
          Privacy Policy
        </a>
        .
        You may opt out of receiving marketing emails at any time.
      </p>
    </div>
  );

  const announcementDefaultState = () => (
    <div className="AnnouncementContent__inner-container">
      <div className="AnnouncementContent__main-content">
        {announcementHeading(heading)}
        {announcementTitle(title)}
        {announcementContent(content)}
        {operation === 1 ? announcementLegalDisclaimer() : ''}
      </div>
      {announcementButtonContainer()}
    </div>
  );

  const announcementLoadingState = () => (
    <div className="AnnouncementContent__loading-wrapper">
      {announcementHeading(heading)}
      {announcementTitle(title)}
      {announcementContent(content)}
      {announcementLegalDisclaimer()}
      {announcementButtonContainer()}
    </div>
  );

  const announcementErrorState = () => {
    const translationKeyErrorStateTitleText = 'marketing_opt_in_error_state_title_text';
    const errorStateFallbackTitleText = 'Failed to Submit. Try Again';
    const translationKeyErrorStateDetailText = 'marketing_opt_in_error_state_detail_text';
    const errorStateFallbackDetailText = 'There was an error submitting this request.';

    return (
      <div className="AnnouncementContent__error-wrapper">
        <Icon
          className="AnnouncementContent__info-icon"
          width="100%"
          height="35px"
          component={Info}
          name="info"
        />
        <TranslationProvider translationKey={translationKeyErrorStateTitleText}>
          <div className="AnnouncementContent__error-wrapper__title-text">
            {errorStateFallbackTitleText}
          </div>
        </TranslationProvider>

        <TranslationProvider translationKey={translationKeyErrorStateDetailText}>
          <div className="AnnouncementContent__error-wrapper__detail-text">
            {errorStateFallbackDetailText}
          </div>
        </TranslationProvider>
        <Button
          onClick={onPrimaryLinkClick}
          variant="primary"
          className="error-button AnnouncementContent__try-again-button"
          data-test-id="announcement-button-retry"
          sm
        >
          <TranslationProvider translationKey="try_again">
            Try Again
          </TranslationProvider>
        </Button>
      </div>
    );
  };

  const announcementOptInSuccess = () => {
    const successHeading = 'Success!';
    const successTitle = 'You’re Signed Up for Resy Emails';
    const successContent = 'Keep an eye on your inbox for intel on new openings, monthly hit lists, and more. You can change your email settings at any time from your Resy profile.';

    return (
      <div>
        {announcementHeading(successHeading)}
        {announcementTitle(successTitle)}
        <div className="AnnouncementContent__success-content">
          {announcementContent(successContent)}
        </div>
        <div className="AnnouncementContent__button-container">
          <Button
            onClick={onSecondaryLinkClick}
            colors={colors.primaryButton}
            data-test-id="announcement-button-close"
          >
            <TranslationProvider translationKey="close">
              Close
            </TranslationProvider>
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div
      className={cx('AnnouncementContent', {
        'AnnouncementContent--mobile': mobile,
        'AnnouncementContent--desktop': !mobile,
        [`AnnouncementContent--${type}`]: true,
      })}
    >
      <div className="AnnouncementContent__main-container">
        {announcementImage()}
        {isDefaultView && announcementDefaultState()}
        {isLoadingView && announcementLoadingState()}
        {isErrorView && announcementErrorState()}
        {isSuccessView && announcementOptInSuccess()}
      </div>
    </div>
  );
};

AnnouncementContent.propTypes = {
  type: PropTypes.oneOf([HEADER, POPUP, FOOTER]),
  heading: PropTypes.string,
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  image: PropTypes.string,
  colors: PropTypes.shape({
    heading: PropTypes.string,
    title: PropTypes.string,
    font: PropTypes.string,
    background: PropTypes.string,
    primaryButton: PropTypes.shape({
      font: PropTypes.string,
      background: PropTypes.string,
    }),
    secondaryButton: PropTypes.shape({
      font: PropTypes.string,
      background: PropTypes.string,
    }),
    linkUrl: PropTypes.string,
  }),
  mobile: PropTypes.bool,
  primaryLinkText: PropTypes.string,
  secondaryLinkText: PropTypes.string,
  onPrimaryLinkClick: PropTypes.func,
  onSecondaryLinkClick: PropTypes.func,
  viewState: PropTypes.oneOf(Object.keys(VIEW_STATE)),
  operation: PropTypes.number,
};

AnnouncementContent.defaultProps = {
  type: POPUP,
  heading: '',
  image: '',
  colors: {
    primaryButton: {},
    secondaryButton: {},
  },
  mobile: false,
  primaryLinkText: '',
  secondaryLinkText: null,
  onPrimaryLinkClick: () => { },
  onSecondaryLinkClick: () => { },
  viewState: VIEW_STATE.DEFAULT,
  operation: undefined,
};

AnnouncementContent.displayName = 'AnnouncementContent';
export default AnnouncementContent;
